import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Paper,
  Card,
  CardContent,
  CardActions,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Alert,
  Breadcrumbs,
  Link,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  NavigateNext,
  Visibility,
  Close,
  WarningAmberOutlined,
  CameraAlt,
  CloudUpload,
  Clear,
  PhotoCamera,
  Cameraswitch,
} from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Webcam from "react-webcam";

// --- Theme ---
const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#FFFFFF", paper: "#FFFFFF" },
    primary: { main: "#C4B08C" },
    error: { main: "#d32f2f" },
    text: { primary: "#111111", secondary: "#555555" },
  },
  typography: { fontFamily: '"Inter", sans-serif' },
});

// --- New AddPhoto Component ---
interface AddPhotoProps {
  onPhotoSelected: (file: File | null) => void;
  photoFile: File | null;
}
const API_URL = process.env.REACT_APP_API_URL;
const IMAGE_URL = process.env.REACT_APP_Image;
const AddPhoto: React.FC<AddPhotoProps> = ({ onPhotoSelected, photoFile }) => {
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user"); // 'user' pour frontale, 'environment' pour arrière
  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      onPhotoSelected(file);
      setIsCameraActive(false);
    }
  };

  const handleCameraCaptureClick = () => {
    onPhotoSelected(null); // Clear any existing file
    setIsCameraActive(true);
  };

  const takePhoto = () => {
    const screenshot = webcamRef.current?.getScreenshot();
    if (screenshot) {
      const byteString = atob(screenshot.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: "image/png" });
      const file = new File([blob], "captured-image.png", {
        type: "image/png",
      });
      onPhotoSelected(file);
      setIsCameraActive(false);
    }
  };

  const cancelPhoto = () => {
    setIsCameraActive(false);
  };

  const handleClearPhoto = () => {
    onPhotoSelected(null);
    setIsCameraActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      onPhotoSelected(file);
      setIsCameraActive(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        border: dragOver ? "2px dashed #C4B08C" : "2px dashed #ccc",
        borderRadius: 2,
        p: 3,
        textAlign: "center",
        bgcolor: dragOver ? "rgba(196,176,140,.08)" : "#fafafa",
        cursor: "pointer",
        minHeight: 250,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageChange}
        accept="image/*"
        style={{ display: "none" }}
      />
      {isCameraActive ? (
        <>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/png"
            videoConstraints={{ facingMode }} // Utilise l'état facingMode
            style={{
              maxWidth: "100%",
              maxHeight: "300px",
              borderRadius: 8,
              transform: "scaleX(-1)", // Inverse l'image si nécessaire
            }}
          />
          <Box display="flex" justifyContent="center" gap={2} mt={2}>
            <Button
              onClick={takePhoto}
              variant="contained"
              startIcon={<PhotoCamera />}
            >
              Take Photo
            </Button>
            <Button
              onClick={() =>
                setFacingMode((prev) =>
                  prev === "user" ? "environment" : "user"
                )
              }
              variant="outlined"
              startIcon={<Cameraswitch />} // Assurez-vous d'importer Cameraswitch depuis @mui/icons-material
            >
              {facingMode === "user"
                ? "Switch to Back Camera"
                : "Switch to Front Camera"}
            </Button>
            <Button
              onClick={cancelPhoto}
              variant="outlined"
              startIcon={<Clear />}
            >
              Cancel
            </Button>
          </Box>
        </>
      ) : photoFile ? (
        <>
          <Box
            component="img"
            src={URL.createObjectURL(photoFile)}
            alt="Preview"
            sx={{ maxWidth: "100%", maxHeight: 200, borderRadius: 2, mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary" mb={1}>
            {photoFile.name}
          </Typography>
          <Box display="flex" justifyContent="center" gap={2}>
            <Button
              onClick={handleClearPhoto}
              variant="text"
              color="error"
              startIcon={<Clear />}
            >
              Clear Photo
            </Button>
          </Box>
        </>
      ) : (
        <>
          <CloudUpload sx={{ color: "#C4B08C", mb: 1, fontSize: 32 }} />
          <Typography variant="body2" color="text.secondary">
            Drag & drop a file here or
          </Typography>
          <Box display="flex" justifyContent="center" gap={2} mt={2}>
            <Button
              variant="outlined"
              startIcon={<CameraAlt />}
              onClick={handleCameraCaptureClick}
            >
              Use Camera
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={handleImageUploadClick}
            >
              Upload from device
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

// --- Main Component ---
const ModelsPage = () => {
  const [models, setModels] = useState<any[]>([]);
  const [engines, setEngines] = useState<any[]>([]);
  const [propellers, setPropellers] = useState<any[]>([]);
  const [speeds, setSpeeds] = useState<any[]>([]);

  const [filterEngines, setFilterEngines] = useState("");
  const [filterModels, setFilterModels] = useState("");
  const [filterPropellers, setFilterPropellers] = useState("");
  const [filterSpeeds, setFilterSpeeds] = useState("");
  const [filterSpeedModel, setFilterSpeedModel] = useState("");
  const [filterSpeedEngine, setFilterSpeedEngine] = useState("");
  const [filterSpeedPropeller, setFilterSpeedPropeller] = useState("");

  const [viewEngines, setViewEngines] = useState("box");
  const [viewModels, setViewModels] = useState("box");
  const [viewPropellers, setViewPropellers] = useState("box");
  const [viewSpeeds, setViewSpeeds] = useState("box");

  const [pageEngines, setPageEngines] = useState(1);
  const [pageModels, setPageModels] = useState(1);
  const [pagePropellers, setPagePropellers] = useState(1);
  const [pageSpeeds, setPageSpeeds] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(6);

  const [modalType, setModalType] = useState<null | string>(null);
  const [modalData, setModalData] = useState<any>({});
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [detailsData, setDetailsData] = useState<any>(null);

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: "model" | "engine" | "propeller" | "speed" | null;
    id: number | null;
  }>({ open: false, type: null, id: null });

  // --- New State for Model Photos ---
  const [modelPhotoFile, setModelPhotoFile] = useState<File | null>(null);

  // --- New State for Validation Errors ---
  const [validationErrors, setValidationErrors] = useState<any>({});

  // --- Predefined fullType options ---
  const fullTypeOptions = [
    "hybride",
    "diesel",
    "essence",
    "électrique",
    "autre",
  ];

  // --- Fetch data ---
  const fetchModels = async () => {
    try {
      const res = await fetch(`${API_URL}/modeles`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setModels(data);
        console.log(data);
      } else {
        setModels([]);
      }
    } catch {
      setAlert({ type: "error", message: "Failed to fetch models" });
      setModels([]);
    }
  };

  const fetchEngines = async () => {
    try {
      const res = await fetch(`${API_URL}/engines`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setEngines(data);
      } else {
        setEngines([]);
      }
    } catch {
      setAlert({ type: "error", message: "Failed to fetch engines" });
      setEngines([]);
    }
  };

  const fetchPropellers = async () => {
    try {
      const res = await fetch(`${API_URL}/propellers`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setPropellers(data);
      } else {
        setPropellers([]);
      }
    } catch {
      setAlert({ type: "error", message: "Failed to fetch propellers" });
      setPropellers([]);
    }
  };

  const fetchSpeeds = async () => {
    try {
      const res = await fetch(`${API_URL}/speeds`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setSpeeds(data);
        console.log(data);
      } else {
        setSpeeds([]);
      }
    } catch {
      setAlert({ type: "error", message: "Failed to fetch speeds" });
      setSpeeds([]);
    }
  };

  useEffect(() => {
    fetchEngines();
    fetchModels();
    fetchPropellers();
    fetchSpeeds();
  }, []);

  // --- Filters & Pagination ---
  const filteredEngines = engines.filter(
    (e: any) =>
      e.engine?.toLowerCase().includes(filterEngines.toLowerCase()) ||
      (e.model?.toLowerCase().includes(filterEngines.toLowerCase()) ?? false)
  );
  const paginatedEngines = filteredEngines.slice(
    (pageEngines - 1) * rowsPerPage,
    pageEngines * rowsPerPage
  );

  const filteredModels = models.filter(
    (m: any) =>
      m.modele?.toLowerCase().includes(filterModels.toLowerCase()) ||
      (m.customRequirements
        ?.toString()
        .toLowerCase()
        .includes(filterModels.toLowerCase()) ??
        false)
  );
  const paginatedModels = filteredModels.slice(
    (pageModels - 1) * rowsPerPage,
    pageModels * rowsPerPage
  );

  const filteredPropellers = propellers.filter((p: any) =>
    p.diameter?.toLowerCase().includes(filterPropellers.toLowerCase())
  );
  const paginatedPropellers = filteredPropellers.slice(
    (pagePropellers - 1) * rowsPerPage,
    pagePropellers * rowsPerPage
  );

  const filteredSpeeds = speeds.filter(
    (s: any) =>
      (filterSpeeds === "" ||
        s.maxSpeed?.toLowerCase().includes(filterSpeeds.toLowerCase()) ||
        s.cruiseSpeed?.toLowerCase().includes(filterSpeeds.toLowerCase())) &&
      (filterSpeedModel === "" || s.modeleId.toString() === filterSpeedModel) &&
      (filterSpeedEngine === "" ||
        s.engineId.toString() === filterSpeedEngine) &&
      (filterSpeedPropeller === "" ||
        s.propellerId.toString() === filterSpeedPropeller)
  );
  const paginatedSpeeds = filteredSpeeds.slice(
    (pageSpeeds - 1) * rowsPerPage,
    pageSpeeds * rowsPerPage
  );

  // --- Modal Handlers ---
  const openModal = async (type: string, data: any = {}) => {
    setModalType(type);
    setValidationErrors({}); // Clear errors
    setModelPhotoFile(null); // Reset photo file on modal open
    if (type.includes("edit")) {
      const fullTypeArray = data.fullType ? data.fullType.split(",") : [];
      setModalData({ ...data, fullType: fullTypeArray });

      // Handle existing image for edit
      if (type.includes("Model") && data.image) {
        try {
          const response = await fetch(`${IMAGE_URL}/modeles/${data.image}`);
          if (response.ok) {
            const blob = await response.blob();
            const file = new File([blob], data.image, { type: blob.type });
            setModelPhotoFile(file);
          } else {
            console.error("Failed to fetch image for editing.");
          }
        } catch (error) {
          console.error("Error fetching image:", error);
        }
      }
    } else {
      let initialData: any = {};
      if (type.includes("Engine")) {
        initialData = { fullType: [] };
      }
      setModalData(initialData);
    }
  };

  const closeModal = () => {
    setModalType(null);
    setModalData({});
    setModelPhotoFile(null);
    setValidationErrors({});
  };
  const openDetailsModal = (data: any) => {
    console.log(data);
    setDetailsData(data);
  };
  const closeDetailsModal = () => setDetailsData(null);

  // --- Validation Logic ---
  const validateForm = () => {
    let errors: any = {};
    let isValid = true;

    if (modalType?.includes("Model")) {
      if (!modalData.modele) {
        errors.modele = "Model name is required.";
        isValid = false;
      }
      if (!modalData.Length || isNaN(Number(modalData.Length))) {
        errors.Length = "Length is required and must be a number.";
        isValid = false;
      }
      if (!modalData.Beam || isNaN(Number(modalData.Beam))) {
        errors.Beam = "Beam is required and must be a number.";
        isValid = false;
      }
      if (!modalData.DryWeight || isNaN(Number(modalData.DryWeight))) {
        errors.DryWeight = "Dry Weight is required and must be a number.";
        isValid = false;
      }
    } else if (modalType?.includes("Engine")) {
      if (!modalData.engine) {
        errors.engine = "Engine name is required.";
        isValid = false;
      }
      if (!modalData.mark) {
        errors.mark = "Mark is required.";
        isValid = false;
      }
      if (!modalData.maxSpeed || isNaN(Number(modalData.maxSpeed))) {
        errors.maxSpeed = "Max Speed is required and must be a number.";
        isValid = false;
      }
      if (!modalData.cruiseSpeed || isNaN(Number(modalData.cruiseSpeed))) {
        errors.cruiseSpeed = "Cruise Speed is required and must be a number.";
        isValid = false;
      }
      if (!modalData.fullType || modalData.fullType.length === 0) {
        errors.fullType = "Fuel Type is required.";
        isValid = false;
      }
    } else if (modalType?.includes("Propeller")) {
      if (!modalData.diameter) {
        errors.diameter = "Diameter is required.";
        isValid = false;
      }
      if (!modalData.length || isNaN(Number(modalData.length))) {
        errors.length = "Length is required and must be a number.";
        isValid = false;
      }
      if (!modalData.angle || isNaN(Number(modalData.angle))) {
        errors.angle = "Angle is required and must be a number.";
        isValid = false;
      }
    } else if (modalType?.includes("Speed")) {
      if (!modalData.modeleId) {
        errors.modeleId = "Model is required.";
        isValid = false;
      }
      if (!modalData.engineId) {
        errors.engineId = "Engine is required.";
        isValid = false;
      }
      if (!modalData.propellerId) {
        errors.propellerId = "Propeller is required.";
        isValid = false;
      }
      if (!modalData.maxSpeed || isNaN(Number(modalData.maxSpeed))) {
        errors.maxSpeed = "Max Speed is required and must be a number.";
        isValid = false;
      }
      if (!modalData.cruiseSpeed || isNaN(Number(modalData.cruiseSpeed))) {
        errors.cruiseSpeed = "Cruise Speed is required and must be a number.";
        isValid = false;
      }
    }

    setValidationErrors(errors);
    return isValid;
  };

  // --- Save Data ---
  const saveData = async () => {
    if (!validateForm()) {
      setAlert({ type: "error", message: "Please correct the form errors." });
      return;
    }

    try {
      let url = "";
      let method = "POST";
      const formData = new FormData();
      let payload = { ...modalData };

      if (modalType?.includes("Engine") && Array.isArray(payload.fullType)) {
        payload.fullType = payload.fullType.join(",");
      }
      // Add other data to FormData
      for (const key in modalData) {
        if (Array.isArray(modalData[key])) {
          formData.append(key, JSON.stringify(modalData[key]));
        } else {
          formData.append(key, modalData[key]);
        }
      }

      // Add photo file if it exists
      if (modalType?.includes("Model") && modelPhotoFile) {
        formData.append("image", modelPhotoFile);
      } else if (
        modalType?.includes("Model") &&
        modalData.image &&
        !modelPhotoFile
      ) {
        // If an image already exists and the user didn't change it, keep the original image name
        formData.append("image", modalData.image);
      } else if (
        modalType?.includes("Model") &&
        !modelPhotoFile &&
        modalData.photoUrl
      ) {
        // If the user clears the image, send a specific flag to the backend to delete it
        formData.append("clearImage", "true");
      }

      if (modalType === "addModel") url = `${API_URL}/modeles`;
      if (modalType === "editModel") {
        url = `${API_URL}/modeles/${modalData.id}`;
        method = "PUT";
      }
      if (modalType === "addEngine") url = `${API_URL}/engines`;
      if (modalType === "editEngine") {
        url = `${API_URL}/engines/${modalData.id}`;
        method = "PUT";
      }
      if (modalType === "addPropeller") url = `${API_URL}/propellers`;
      if (modalType === "editPropeller") {
        url = `${API_URL}/propellers/${modalData.id}`;
        method = "PUT";
      }
      if (modalType === "addSpeed") url = `${API_URL}/speeds`;
      if (modalType === "editSpeed") {
        url = `${API_URL}/speeds/${modalData.id}`;
        method = "PUT";
      }
      if (modalType === "addModel") {
        await fetch(url, { method, body: formData });
      } else if (modalType === "editModel") {
        await fetch(url, { method, body: formData });
      } else {
        await fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      // await fetch(url, { method, body: modalData });
      await fetchModels();
      await fetchEngines();
      await fetchPropellers();
      await fetchSpeeds();

      closeModal();

      setAlert({ type: "success", message: "Saved successfully" });
    } catch {
      setAlert({ type: "error", message: "Save failed" });
    }
  };

  // --- Delete Data ---
  const handleDeleteClick = (
    type: "model" | "engine" | "propeller" | "speed",
    id: number
  ) => {
    setDeleteDialog({ open: true, type, id });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.type || !deleteDialog.id) return;

    try {
      const url =
        deleteDialog.type === "model"
          ? `${API_URL}/modeles/${deleteDialog.id}`
          : deleteDialog.type === "engine"
          ? `${API_URL}/engines/${deleteDialog.id}`
          : deleteDialog.type === "propeller"
          ? `${API_URL}/propellers/${deleteDialog.id}`
          : `${API_URL}/speeds/${deleteDialog.id}`;

      await fetch(url, { method: "DELETE" });
      setDeleteDialog({ open: false, type: null, id: null });
      fetchModels();
      fetchEngines();
      fetchPropellers();
      fetchSpeeds();
      setAlert({ type: "success", message: "Deleted successfully" });
    } catch {
      setAlert({ type: "error", message: "Delete failed" });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ py: 4, bgcolor: "#FFFFFF" }}>
        {alert.message && (
          <Alert severity={alert.type as any} sx={{ mb: 3 }}>
            {alert.message}
          </Alert>
        )}

        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link color="inherit" href="#" underline="hover">
            HOME
          </Link>
          <Typography color="text.primary">MODELS</Typography>
        </Breadcrumbs>

        <Typography
          variant="h4"
          fontWeight="bold"
          textAlign="left"
          sx={{ mb: 6 }}
        >
          🚀 Models & Engines Management
        </Typography>

        {/* --- Engines Section --- */}
        <Box mb={6}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={2}
            gap={2}
          >
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Engines
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                size="small"
                placeholder="Search engines..."
                value={filterEngines}
                onChange={(e) => setFilterEngines(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  width: { xs: "100%", sm: "auto" },
                }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{ bgcolor: "#C4B08C", "&:hover": { bgcolor: "#b39d77" } }}
                onClick={() => openModal("addEngine")}
              >
                Add Engine
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  setViewEngines(viewEngines === "table" ? "box" : "table")
                }
              >
                Toggle View
              </Button>
            </Box>
          </Box>
          <Divider sx={{ my: 2, borderColor: "#eaeaea" }} />

          {/* --- Engine List --- */}
          {viewEngines === "table" ? (
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,.08)" }}
            >
              <Table>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Engine</TableCell>
                    <TableCell>Max Speed</TableCell>
                    <TableCell>Cruise Speed</TableCell>
                    <TableCell>Fuel Type</TableCell>
                    <TableCell>Max Speed by Model</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedEngines.map((e: any) => (
                    <TableRow key={e.id}>
                      <TableCell>{e.engine}</TableCell>
                      <TableCell>{e.maxSpeed || "-"}</TableCell>
                      <TableCell>{e.cruiseSpeed || "-"}</TableCell>
                      <TableCell>{e.fullType || "-"}</TableCell>
                      <TableCell>
                        {typeof e.maxSpeedByModel === "object"
                          ? JSON.stringify(e.maxSpeedByModel)
                          : e.maxSpeedByModel || "-"}
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => openModal("editEngine", e)}>
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => openDetailsModal(e)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          sx={{ color: "#d32f2f" }}
                          onClick={() => handleDeleteClick("engine", e.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={2}>
              {paginatedEngines.map((e: any) => (
                <Box
                  key={e.id}
                  sx={{ flex: "1 1 calc(25% - 16px)", minWidth: 200 }}
                >
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: "0 6px 20px rgba(0,0,0,.08)",
                      transition: "transform 0.2s",
                      "&:hover": { transform: "translateY(-4px)" },
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6">{e.engine}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Max: {e.maxSpeed || "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cruise: {e.cruiseSpeed || "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fuel Type: {e.fullType || "-"}
                      </Typography>
                    </CardContent>
                    <Divider sx={{ borderColor: "#eaeaea" }} />
                    <CardActions>
                      <IconButton onClick={() => openModal("editEngine", e)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => openDetailsModal(e)}
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        sx={{ color: "#d32f2f" }}
                        onClick={() => handleDeleteClick("engine", e.id)}
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Box>
              ))}
            </Box>
          )}

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(filteredEngines.length / rowsPerPage)}
              page={pageEngines}
              onChange={(e, v) => setPageEngines(v)}
              color="primary"
            />
          </Box>
        </Box>
        <Divider sx={{ my: 5 }} />

        {/* --- Models Section --- */}
        <Box mb={6}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={2}
            gap={2}
          >
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Models
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                size="small"
                placeholder="Search models..."
                value={filterModels}
                onChange={(e) => setFilterModels(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  width: { xs: "100%", sm: "auto" },
                }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{ bgcolor: "#C4B08C", "&:hover": { bgcolor: "#b39d77" } }}
                onClick={() => openModal("addModel")}
              >
                Add Model
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  setViewModels(viewModels === "table" ? "box" : "table")
                }
              >
                Toggle View
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor: "#eaeaea" }} />

          {/* --- Model List --- */}
          {viewModels === "table" ? (
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,.08)" }}
            >
              <Table>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell>Engines</TableCell>
                    <TableCell>Custom Requirements</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedModels.map((m: any) => (
                    <TableRow key={m.id}>
                      <TableCell>{m.modele}</TableCell>
                      <TableCell>
                        {engines
                          .filter((e: any) => m.engineIds?.includes(e.id))
                          .map((e: any) => e.engine)
                          .join(", ") || "-"}
                      </TableCell>
                      <TableCell>
                        {m.customRequirements?.join(", ") || "-"}
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => openModal("editModel", m)}>
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => openDetailsModal(m)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          sx={{ color: "#d32f2f" }}
                          onClick={() => handleDeleteClick("model", m.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={2}>
              {paginatedModels.map((m: any) => (
                <Box
                  key={m.id}
                  sx={{ flex: "1 1 calc(33.333% - 16px)", minWidth: 250 }}
                >
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: "0 6px 20px rgba(0,0,0,.08)",
                      transition: "transform 0.2s",
                      "&:hover": { transform: "translateY(-4px)" },
                    }}
                  >
                    {m.image && (
                      <Box
                        sx={{
                          p: 2,
                          pb: 0,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          component="img"
                          src={`${IMAGE_URL}/modeles/${m.image}`}
                          alt={m.modele}
                          sx={{
                            width: "100%",
                            maxHeight: 150,
                            objectFit: "cover",
                            borderRadius: 2,
                          }}
                        />
                      </Box>
                    )}
                    <CardContent>
                      <Typography variant="h6">{m.modele}</Typography>
                      <Typography variant="body2">
                        Engines:{" "}
                        {engines
                          .filter((e: any) => m.engineIds?.includes(e.id))
                          .map((e: any) => e.engine)
                          .join(", ") || "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Custom: {m.customRequirements?.join(", ") || "-"}
                      </Typography>
                    </CardContent>
                    <Divider sx={{ borderColor: "#eaeaea" }} />
                    <CardActions>
                      <IconButton onClick={() => openModal("editModel", m)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => openDetailsModal(m)}
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        sx={{ color: "#d32f2f" }}
                        onClick={() => handleDeleteClick("model", m.id)}
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Box>
              ))}
            </Box>
          )}

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(filteredModels.length / rowsPerPage)}
              page={pageModels}
              onChange={(e, v) => setPageModels(v)}
              color="primary"
            />
          </Box>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* --- Propellers Section --- */}
        <Box mb={6}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={2}
            gap={2}
          >
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Propellers
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                size="small"
                placeholder="Search propellers..."
                value={filterPropellers}
                onChange={(e) => setFilterPropellers(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  width: { xs: "100%", sm: "auto" },
                }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{ bgcolor: "#C4B08C", "&:hover": { bgcolor: "#b39d77" } }}
                onClick={() => openModal("addPropeller")}
              >
                Add Propeller
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  setViewPropellers(
                    viewPropellers === "table" ? "box" : "table"
                  )
                }
              >
                Toggle View
              </Button>
            </Box>
          </Box>
          <Divider sx={{ my: 2, borderColor: "#eaeaea" }} />

          {/* --- Propeller List --- */}
          {viewPropellers === "table" ? (
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,.08)" }}
            >
              <Table>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Diameter</TableCell>
                    <TableCell>Length</TableCell>
                    <TableCell>Angle</TableCell>
                    <TableCell>Inverse</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedPropellers.map((p: any) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.diameter}</TableCell>
                      <TableCell>{p.length}</TableCell>
                      <TableCell>{p.angle}</TableCell>
                      <TableCell>{p.inverse ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => openModal("editPropeller", p)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => openDetailsModal(p)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          sx={{ color: "#d32f2f" }}
                          onClick={() => handleDeleteClick("propeller", p.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={2}>
              {paginatedPropellers.map((p: any) => (
                <Box
                  key={p.id}
                  sx={{ flex: "1 1 calc(25% - 16px)", minWidth: 200 }}
                >
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: "0 6px 20px rgba(0,0,0,.08)",
                      transition: "transform 0.2s",
                      "&:hover": { transform: "translateY(-4px)" },
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6">{p.diameter}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Length: {p.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Angle: {p.angle}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inverse: {p.inverse ? "Yes" : "No"}
                      </Typography>
                    </CardContent>
                    <Divider sx={{ borderColor: "#eaeaea" }} />
                    <CardActions>
                      <IconButton onClick={() => openModal("editPropeller", p)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => openDetailsModal(p)}
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        sx={{ color: "#d32f2f" }}
                        onClick={() => handleDeleteClick("propeller", p.id)}
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Box>
              ))}
            </Box>
          )}

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(filteredPropellers.length / rowsPerPage)}
              page={pagePropellers}
              onChange={(e, v) => setPagePropellers(v)}
              color="primary"
            />
          </Box>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* --- Speeds Section --- */}
        <Box mb={6}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={2}
            gap={2}
          >
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Speeds
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{ bgcolor: "#C4B08C", "&:hover": { bgcolor: "#b39d77" } }}
                onClick={() => openModal("addSpeed")}
              >
                Add Speed
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  setViewSpeeds(viewSpeeds === "table" ? "box" : "table")
                }
              >
                Toggle View
              </Button>
            </Box>
          </Box>
          <Divider sx={{ my: 2, borderColor: "#eaeaea" }} />

          {/* New Filter Section without Grid */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="text.primary"
              sx={{ mb: 2 }}
            >
              Filters
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                placeholder="Search speeds..."
                value={filterSpeeds}
                onChange={(e) => setFilterSpeeds(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: "#f5f5f5", borderRadius: 2, flex: "1 1 200px" }}
              />
              <FormControl
                size="small"
                sx={{ flex: "1 1 200px", bgcolor: "#f5f5f5", borderRadius: 2 }}
              >
                <InputLabel>Model</InputLabel>
                <Select
                  value={filterSpeedModel}
                  label="Model"
                  onChange={(e) => setFilterSpeedModel(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Models</em>
                  </MenuItem>
                  {models.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.modele}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{ flex: "1 1 200px", bgcolor: "#f5f5f5", borderRadius: 2 }}
              >
                <InputLabel>Engine</InputLabel>
                <Select
                  value={filterSpeedEngine}
                  label="Engine"
                  onChange={(e) => setFilterSpeedEngine(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Engines</em>
                  </MenuItem>
                  {engines.map((e) => (
                    <MenuItem key={e.id} value={e.id}>
                      {e.engine}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{ flex: "1 1 200px", bgcolor: "#f5f5f5", borderRadius: 2 }}
              >
                <InputLabel>Propeller</InputLabel>
                <Select
                  value={filterSpeedPropeller}
                  label="Propeller"
                  onChange={(e) => setFilterSpeedPropeller(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Propellers</em>
                  </MenuItem>
                  {propellers.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.diameter}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* --- Speed List --- */}
          {viewSpeeds === "table" ? (
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,.08)" }}
            >
              <Table>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell>Engine</TableCell>
                    <TableCell>Propeller</TableCell>
                    <TableCell>Max Speed</TableCell>
                    <TableCell>Cruise Speed</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSpeeds.map((s: any) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        {models.find((m) => m.id === s.modeleId)?.modele || "-"}
                      </TableCell>
                      <TableCell>
                        {engines.find((e) => e.id === s.engineId)?.engine ||
                          "-"}
                      </TableCell>
                      <TableCell>
                        {propellers.find((p) => p.id === s.propellerId)
                          ?.diameter || "-"}
                      </TableCell>
                      <TableCell>{s.maxSpeed}</TableCell>
                      <TableCell>{s.cruiseSpeed}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => openModal("editSpeed", s)}>
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => openDetailsModal(s)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          sx={{ color: "#d32f2f" }}
                          onClick={() => handleDeleteClick("speed", s.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={2}>
              {paginatedSpeeds.map((s: any) => (
                <Box
                  key={s.id}
                  sx={{ flex: "1 1 calc(33.333% - 16px)", minWidth: 250 }}
                >
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: "0 6px 20px rgba(0,0,0,.08)",
                      transition: "transform 0.2s",
                      "&:hover": { transform: "translateY(-4px)" },
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6">
                        Max Speed: {s.maxSpeed}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cruise Speed: {s.cruiseSpeed}
                      </Typography>
                      <Typography variant="body2">
                        Model:{" "}
                        {models.find((m) => m.id === s.modeleId)?.modele || "-"}
                      </Typography>
                      <Typography variant="body2">
                        Engine:{" "}
                        {engines.find((e) => e.id === s.engineId)?.engine ||
                          "-"}
                      </Typography>
                      <Typography variant="body2">
                        Propeller:{" "}
                        {propellers.find((p) => p.id === s.propellerId)
                          ?.diameter || "-"}
                      </Typography>
                    </CardContent>
                    <Divider sx={{ borderColor: "#eaeaea" }} />
                    <CardActions>
                      <IconButton onClick={() => openModal("editSpeed", s)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => openDetailsModal(s)}
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        sx={{ color: "#d32f2f" }}
                        onClick={() => handleDeleteClick("speed", s.id)}
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Box>
              ))}
            </Box>
          )}

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(filteredSpeeds.length / rowsPerPage)}
              page={pageSpeeds}
              onChange={(e, v) => setPageSpeeds(v)}
              color="primary"
            />
          </Box>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* --- Modal Add/Edit --- */}
        {modalType && (
          <Dialog
            open
            onClose={(event, reason) => {
              // Only close when the X button is clicked
              if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                closeModal();
              }
            }}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                borderRadius: 4,
                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                overflow: "hidden",
              },
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #C4B08C 0%, #9F8C6B 100%)",
                p: 3,
                position: "relative",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                {modalType.includes("Add") ? "Add " : "Edit "}
                {modalType.includes("Model")
                  ? "Model"
                  : modalType.includes("Engine")
                  ? "Engine"
                  : modalType.includes("Propeller")
                  ? "Propeller"
                  : "Speed"}
              </Typography>
              <IconButton onClick={closeModal} sx={{ color: "white" }}>
                <Close />
              </IconButton>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "radial-gradient(circle at top right, rgba(255,255,255,0.2) 0%, transparent 70%)",
                  zIndex: 1,
                }}
              />
            </Box>
            <DialogContent sx={{ pt: 4, pb: 3, px: 4 }}>
              {/* Model Modal Form */}
              {modalType.includes("Model") ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Typography variant="h6" mb={1} fontWeight="bold">
                    Model Details
                  </Typography>
                  <TextField
                    fullWidth
                    label="Model Name"
                    required
                    value={modalData.modele || ""}
                    onChange={(e) =>
                      setModalData({ ...modalData, modele: e.target.value })
                    }
                    error={!!validationErrors.modele}
                    helperText={validationErrors.modele}
                  />
                  <Box sx={{ display: "flex", gap: 3 }}>
                    <TextField
                      fullWidth
                      type="number"
                      required
                      label="Length (m)"
                      value={modalData.Length || ""}
                      onChange={(e) =>
                        setModalData({ ...modalData, Length: e.target.value })
                      }
                      error={!!validationErrors.Length}
                      helperText={validationErrors.Length}
                    />
                    <TextField
                      fullWidth
                      type="number"
                      required
                      label="Beam (m)"
                      value={modalData.Beam || ""}
                      onChange={(e) =>
                        setModalData({ ...modalData, Beam: e.target.value })
                      }
                      error={!!validationErrors.Beam}
                      helperText={validationErrors.Beam}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    type="number"
                    required
                    label="Dry Weight (kg)"
                    value={modalData.DryWeight || ""}
                    onChange={(e) =>
                      setModalData({ ...modalData, DryWeight: e.target.value })
                    }
                    error={!!validationErrors.DryWeight}
                    helperText={validationErrors.DryWeight}
                  />

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6" mb={1} fontWeight="bold">
                    Model Photo
                  </Typography>
                  <AddPhoto
                    onPhotoSelected={setModelPhotoFile}
                    photoFile={modelPhotoFile}
                  />
                  <Divider sx={{ my: 2 }} />

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: "#f8f8f8",
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel id="engine-multiple-checkbox-label">
                        Available Engines
                      </InputLabel>
                      <Select
                        labelId="engine-multiple-checkbox-label"
                        id="engine-multiple-checkbox"
                        multiple
                        value={modalData.engineIds || []}
                        onChange={(event) => {
                          const {
                            target: { value },
                          } = event;
                          setModalData({
                            ...modalData,
                            engineIds:
                              typeof value === "string"
                                ? value.split(",").map(Number)
                                : value,
                          });
                        }}
                        input={<OutlinedInput label="Available Engines" />}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((value: number) => (
                              <Chip
                                key={value}
                                label={
                                  engines.find((e) => e.id === value)?.engine
                                }
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {engines.map((e) => (
                          <MenuItem key={e.id} value={e.id}>
                            <Checkbox
                              checked={
                                (modalData.engineIds || []).indexOf(e.id) > -1
                              }
                            />
                            {e.engine}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Paper>
                </Box>
              ) : /* Engine Modal Form */ modalType.includes("Engine") ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box sx={{ display: "flex", gap: 3 }}>
                    <TextField
                      fullWidth
                      label="Engine Name"
                      required
                      value={modalData.engine || ""}
                      onChange={(e) =>
                        setModalData({ ...modalData, engine: e.target.value })
                      }
                      error={!!validationErrors.engine}
                      helperText={validationErrors.engine}
                    />
                    <TextField
                      fullWidth
                      label="Mark"
                      required
                      value={modalData.mark || ""}
                      onChange={(e) =>
                        setModalData({ ...modalData, mark: e.target.value })
                      }
                      error={!!validationErrors.mark}
                      helperText={validationErrors.mark}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 3 }}>
                    <TextField
                      fullWidth
                      label="Max Speed"
                      required
                      value={modalData.maxSpeed || ""}
                      onChange={(e) =>
                        setModalData({ ...modalData, maxSpeed: e.target.value })
                      }
                      error={!!validationErrors.maxSpeed}
                      helperText={validationErrors.maxSpeed}
                    />
                    <TextField
                      fullWidth
                      label="Cruise Speed"
                      required
                      value={modalData.cruiseSpeed || ""}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          cruiseSpeed: e.target.value,
                        })
                      }
                      error={!!validationErrors.cruiseSpeed}
                      helperText={validationErrors.cruiseSpeed}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={modalData.engineDescription || ""}
                    onChange={(e) =>
                      setModalData({
                        ...modalData,
                        engineDescription: e.target.value,
                      })
                    }
                  />

                  <FormControl
                    fullWidth
                    required
                    error={!!validationErrors.fullType}
                  >
                    <InputLabel id="full-type-multiple-checkbox-label">
                      Fuel Type
                    </InputLabel>
                    <Select
                      labelId="full-type-multiple-checkbox-label"
                      id="full-type-multiple-checkbox"
                      multiple
                      value={modalData.fullType || []}
                      onChange={(event) => {
                        const {
                          target: { value },
                        } = event;
                        setModalData({
                          ...modalData,
                          fullType:
                            typeof value === "string"
                              ? value.split(",")
                              : value,
                        });
                      }}
                      input={<OutlinedInput label="Fuel Type" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value: string) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {fullTypeOptions.map((type) => (
                        <MenuItem key={type} value={type}>
                          <Checkbox
                            checked={
                              (modalData.fullType || []).indexOf(type) > -1
                            }
                          />
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                    {validationErrors.fullType && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ ml: 2, mt: 0.5 }}
                      >
                        {validationErrors.fullType}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
              ) : /* Propeller Modal Form */ modalType.includes("Propeller") ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Propeller Diameter"
                    required
                    value={modalData.diameter || ""}
                    onChange={(e) =>
                      setModalData({ ...modalData, diameter: e.target.value })
                    }
                    error={!!validationErrors.diameter}
                    helperText={validationErrors.diameter}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    required
                    label="Length"
                    value={modalData.length || ""}
                    onChange={(e) =>
                      setModalData({ ...modalData, length: e.target.value })
                    }
                    error={!!validationErrors.length}
                    helperText={validationErrors.length}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    required
                    label="Angle"
                    value={modalData.angle || ""}
                    onChange={(e) =>
                      setModalData({ ...modalData, angle: e.target.value })
                    }
                    error={!!validationErrors.angle}
                    helperText={validationErrors.angle}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={modalData.inverse || false}
                        onChange={(e) =>
                          setModalData({
                            ...modalData,
                            inverse: e.target.checked,
                          })
                        }
                      />
                    }
                    label={<Typography>Is Inverse?</Typography>}
                  />
                </Box>
              ) : /* Speed Modal Form */ modalType.includes("Speed") ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <FormControl
                    fullWidth
                    required
                    error={!!validationErrors.modeleId}
                  >
                    <InputLabel>Model</InputLabel>
                    <Select
                      value={modalData.modeleId || ""}
                      label="Model"
                      onChange={(e) =>
                        setModalData({ ...modalData, modeleId: e.target.value })
                      }
                    >
                      {models.map((m) => (
                        <MenuItem key={m.id} value={m.id}>
                          {m.modele}
                        </MenuItem>
                      ))}
                    </Select>
                    {validationErrors.modeleId && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ ml: 2, mt: 0.5 }}
                      >
                        {validationErrors.modeleId}
                      </Typography>
                    )}
                  </FormControl>
                  <FormControl
                    fullWidth
                    required
                    error={!!validationErrors.engineId}
                  >
                    <InputLabel>Engine</InputLabel>
                    <Select
                      value={modalData.engineId || ""}
                      label="Engine"
                      onChange={(e) =>
                        setModalData({ ...modalData, engineId: e.target.value })
                      }
                    >
                      {engines.map((e) => (
                        <MenuItem key={e.id} value={e.id}>
                          {e.engine}
                        </MenuItem>
                      ))}
                    </Select>
                    {validationErrors.engineId && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ ml: 2, mt: 0.5 }}
                      >
                        {validationErrors.engineId}
                      </Typography>
                    )}
                  </FormControl>
                  <FormControl
                    fullWidth
                    required
                    error={!!validationErrors.propellerId}
                  >
                    <InputLabel>Propeller</InputLabel>
                    <Select
                      value={modalData.propellerId || ""}
                      label="Propeller"
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          propellerId: e.target.value,
                        })
                      }
                    >
                      {propellers.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.diameter}
                        </MenuItem>
                      ))}
                    </Select>
                    {validationErrors.propellerId && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ ml: 2, mt: 0.5 }}
                      >
                        {validationErrors.propellerId}
                      </Typography>
                    )}
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Max Speed"
                    required
                    value={modalData.maxSpeed || ""}
                    onChange={(e) =>
                      setModalData({ ...modalData, maxSpeed: e.target.value })
                    }
                    error={!!validationErrors.maxSpeed}
                    helperText={validationErrors.maxSpeed}
                  />
                  <TextField
                    fullWidth
                    label="Cruise Speed"
                    required
                    value={modalData.cruiseSpeed || ""}
                    onChange={(e) =>
                      setModalData({
                        ...modalData,
                        cruiseSpeed: e.target.value,
                      })
                    }
                    error={!!validationErrors.cruiseSpeed}
                    helperText={validationErrors.cruiseSpeed}
                  />
                </Box>
              ) : null}
            </DialogContent>
            <DialogActions
              sx={{
                px: 4,
                py: 2.5,
                borderTop: "1px solid #e0e0e0",
                bgcolor: "#f9f9f9",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={closeModal}
                variant="text"
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  background:
                    "linear-gradient(135deg, #C4B08C 0%, #9F8C6B 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #b39d77 0%, #8a7a5c 100%)",
                  },
                  borderRadius: 2,
                  px: 3,
                  boxShadow: "0 4px 10px rgba(196, 176, 140, 0.3)",
                }}
                onClick={saveData}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* --- Details Modal --- */}
        {detailsData && (
          <Dialog
            open
            onClose={(event, reason) => {
              // Only close when the X button is clicked
              if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                closeDetailsModal();
              }
            }}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                overflow: "hidden",
              },
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #C4B08C 0%, #9F8C6B 100%)",
                p: 2.5,
                position: "relative",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                {detailsData.modele ||
                  detailsData.engine ||
                  detailsData.diameter ||
                  `Speed #${detailsData.id}`}
              </Typography>
              <IconButton
                onClick={closeDetailsModal}
                sx={{ color: "white", zIndex: 2 }}
              >
                <Close />
              </IconButton>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "radial-gradient(circle at top right, rgba(255,255,255,0.2) 0%, transparent 70%)",
                  zIndex: 1,
                }}
              />
            </Box>
            <DialogContent sx={{ p: 0 }}>
              {detailsData.modele ? (
                <Box display="flex" flexDirection="column" gap={0}>
                  {detailsData.image && (
                    <Box
                      sx={{
                        p: 3,
                        display: "flex",
                        justifyContent: "center",
                        bgcolor: "#f5f5f5",
                        borderBottom: "1px solid #eaeaea",
                      }}
                    >
                      <Box
                        component="img"
                        src={`${IMAGE_URL}/modeles/${detailsData.image}`}
                        alt={detailsData.modele}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: 300,
                          borderRadius: 2,
                        }}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      borderBottom: "1px solid #eaeaea",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "1 1 50%",
                        p: 3,
                        borderRight: "1px solid #eaeaea",
                        borderBottom: { xs: "1px solid #eaeaea", md: "none" },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ mb: 2, color: "#C4B08C" }}
                      >
                        Specifications
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: "8px 16px",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Length:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.Length || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Beam:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.Beam || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Dry Weight:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.DryWeight || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Draft:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.DraftDriveUp || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Bridge Clearance:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.BridgeClearance || "-"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: "1 1 50%", p: 3 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ mb: 2, color: "#C4B08C" }}
                      >
                        Compatible Engines
                      </Typography>
                      {engines.filter((e) =>
                        detailsData.engineIds?.includes(e.id)
                      ).length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {engines
                            .filter((e) =>
                              detailsData.engineIds?.includes(e.id)
                            )
                            .map((e) => (
                              <Paper
                                key={e.id}
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  bgcolor: "rgba(196, 176, 140, 0.08)",
                                  border: "1px solid rgba(196, 176, 140, 0.2)",
                                }}
                              >
                                <Typography variant="body2" fontWeight={500}>
                                  {e.engine}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Max: {e.maxSpeed || "-"} • Cruise:{" "}
                                  {e.cruiseSpeed || "-"}
                                </Typography>
                              </Paper>
                            ))}
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          No engines assigned
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ mb: 2, color: "#C4B08C" }}
                    >
                      Custom Requirements
                    </Typography>
                    {detailsData.customRequirements?.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {detailsData.customRequirements.map(
                          (req: string, index: number) => (
                            <Chip
                              key={index}
                              label={req}
                              sx={{
                                bgcolor: "rgba(196, 176, 140, 0.1)",
                                color: "#9F8C6B",
                                borderRadius: "16px",
                              }}
                            />
                          )
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        No custom requirements specified
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : detailsData.engine ? (
                <Box display="flex" flexDirection="column" gap={0}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      borderBottom: "1px solid #eaeaea",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "1 1 50%",
                        p: 3,
                        borderRight: "1px solid #eaeaea",
                        borderBottom: { xs: "1px solid #eaeaea", md: "none" },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ mb: 2, color: "#C4B08C" }}
                      >
                        Engine Details
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: "8px 16px",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Max Speed:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.maxSpeed || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cruise Speed:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.cruiseSpeed || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Fuel Type:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.fullType || "-"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: "1 1 50%", p: 3 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ mb: 2, color: "#C4B08C" }}
                      >
                        Description
                      </Typography>
                      <Typography variant="body2">
                        {detailsData.engineDescription ||
                          "No description available"}
                      </Typography>
                    </Box>
                  </Box>
                  {detailsData.maxSpeedByModel &&
                    Object.keys(detailsData.maxSpeedByModel).length > 0 && (
                      <Box sx={{ p: 3 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{ mb: 2, color: "#C4B08C" }}
                        >
                          Max Speed by Model
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {Object.entries(detailsData.maxSpeedByModel).map(
                            ([modelId, speed]: [string, any]) => {
                              const model = models.find(
                                (m) => m.id.toString() === modelId
                              );
                              return model ? (
                                <Box
                                  key={modelId}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: "rgba(196, 176, 140, 0.08)",
                                    border:
                                      "1px solid rgba(196, 176, 140, 0.2)",
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={500}>
                                    {model.modele}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    color="#9F8C6B"
                                  >
                                    {speed}
                                  </Typography>
                                </Box>
                              ) : null;
                            }
                          )}
                        </Box>
                      </Box>
                    )}
                </Box>
              ) : detailsData.diameter ? (
                <Box display="flex" flexDirection="column" gap={0}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      borderBottom: "1px solid #eaeaea",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "1 1 50%",
                        p: 3,
                        borderRight: "1px solid #eaeaea",
                        borderBottom: { xs: "1px solid #eaeaea", md: "none" },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ mb: 2, color: "#C4B08C" }}
                      >
                        Propeller Details
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: "8px 16px",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Diameter:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.diameter || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Length:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.length || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Angle:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.angle || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Inverse:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.inverse ? "Yes" : "No"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={0}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      borderBottom: "1px solid #eaeaea",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "1 1 50%",
                        p: 3,
                        borderRight: "1px solid #eaeaea",
                        borderBottom: { xs: "1px solid #eaeaea", md: "none" },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ mb: 2, color: "#C4B08C" }}
                      >
                        Speed Details
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: "8px 16px",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Max Speed:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.maxSpeed || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Cruise Speed:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {detailsData.cruiseSpeed || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Model:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {models.find((m) => m.id === detailsData.modeleId)
                            ?.modele || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Engine:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {engines.find((e) => e.id === detailsData.engineId)
                            ?.engine || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Propeller:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {propellers.find(
                            (p) => p.id === detailsData.propellerId
                          )?.diameter || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions
              sx={{ p: 2, borderTop: "1px solid #eaeaea", bgcolor: "#f9f9f9" }}
            >
              <Button
                onClick={closeDetailsModal}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: "#C4B08C",
                  color: "#C4B08C",
                  "&:hover": {
                    borderColor: "#b39d77",
                    bgcolor: "rgba(196, 176, 140, 0.04)",
                  },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* --- Confirm Delete Dialog --- */}
        <Dialog
          open={deleteDialog.open}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              setDeleteDialog({ open: false, type: null, id: null });
            }
          }}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: { borderRadius: 3, boxShadow: "0 10px 40px rgba(0,0,0,0.15)" },
          }}
        >
          <DialogContent sx={{ p: 4, textAlign: "center" }}>
            <Box sx={{ color: "error.main", mb: 2 }}>
              <WarningAmberOutlined sx={{ fontSize: 64 }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Confirm Deletion
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to delete this {deleteDialog.type}? This
              action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
            <Button
              variant="outlined"
              onClick={() =>
                setDeleteDialog({ open: false, type: null, id: null })
              }
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDelete}
              sx={{
                bgcolor: "error.main",
                "&:hover": { bgcolor: "#c00" },
                borderRadius: 2,
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default ModelsPage;
